/**
 * User: n.gorte
 * Date: 11.07.2022
 * Time: 10:45
 */

import React, { useRef, useState } from 'external/react';
import PropTypes from 'external/prop-types';
import { isLayerSet } from '../utils/map-config-util';
import { AmdRequireComponent } from 'plugin/liferay-widgets-react-web';
import { Badge, Button, Overlay } from 'external/react-bootstrap';

function getCount(layerSet) {
    return layerSet.children.reduce((acc, child) => {
        return child.type === 'LayerSet' ? acc + getCount(child) : acc + 1;
    }, 0);
}

function getActiveCount(layerSet, activeUuids) {
    return layerSet.children.reduce((acc, child) => {
        if (child.type === 'LayerSet') {
            return acc + getActiveCount(child, activeUuids);
        } else if (activeUuids && Object.keys(activeUuids).includes(child.uuid)) {
            return acc + 1;
        }
        return acc;
    }, 0);
}

const CheckIcon = ({ checked }) => (<span className={checked ? 'fa fa-check-square' : 'fa fa-square-o'}/>);

const SwitcherIcon = ({ expanded, isLeaf }) => (
    !isLeaf && <span className={expanded ? 'icon icon-fliwas-arrow-down' : 'icon icon-fliwas-arrow-right'}/>
);

export const LayerChooser = ({ layerDescriptions, onLayersChange, activeLayerUuids = {}, mapId }) => {
    const mapContainer = document.getElementById(mapId);
    const overlayContainer = useRef(mapContainer || document.body);
    const popupTarget = useRef(null);
    const infoText = useRef(null);
    const [ showInfo, setShowInfo ] = useState(false);

    const onCheck = (checkedKeys, { checked, node }) => {
        if (checkedKeys) {
            const parentNode = findParentNode(treeData, node.key);

            if (parentNode && parentNode.isSingleChoice) {
                const siblingKeys = parentNode.children
                    .map(child => child.key)
                    .filter(key => key !== node.key && checkedKeys.includes(key));

                if (siblingKeys.length > 0) {
                    checkedKeys = checkedKeys.filter(key => !siblingKeys.includes(key));
                }
            }

            const newActiveUuids = checkedKeys
                .filter(key => !key.includes('LayerSet'))
                .reduce((acc, key) => {
                    acc[key] = true;
                    return acc;
                }, {});

            onLayersChange(newActiveUuids);
        }
    };

    const renderLayerSet = (layerSet) => {
        const count = getCount(layerSet);
        const activeCount = getActiveCount(layerSet, activeLayerUuids);
        return (
            <React.Fragment>
               <span className="rc-tree-title__layerset">
                {layerSet.title}
                </span>
                <Badge className="ml-2">
                    {activeCount}/{count}
                </Badge>
                {
                    layerSet.description.length > 0 && (
                        <Button
                            variant="outline-primary"
                            className="rc-tree-title__info-button"
                            onClick={showInfoPopup(layerSet.description)}
                        >
                            <span className="icon icon-fliwas-info-o"/>
                        </Button>
                    )
                }
            </React.Fragment>
        );
    };

    const showInfoPopup = (text) => (e) => {
        e.stopPropagation();
        e.preventDefault();
        popupTarget.current = e.target;
        infoText.current = text;
        setShowInfo(!showInfo);
    };

    const renderLogicalLayer = ({ title }, config = {}) => {
        const { infoText } = config;
        return (
            <React.Fragment>
                <span className="rc-tree-title__logical_layer">{title}</span>
                {
                    infoText && (
                        <Button
                            variant="outline-primary"
                            className="rc-tree-title__info-button"
                            onClick={showInfoPopup(infoText)}
                        >
                            <span className="icon icon-fliwas-info-o"/>
                        </Button>
                    )
                }
            </React.Fragment>
        );
    };

    const getTreeData = (layers) => {
        return layers.map((item, i) => {
            const isLayersSet = isLayerSet(item);
            let configJson;
            try {
                configJson = isLayersSet
                    ? item.config && JSON.parse(item.config)
                    : item.children && JSON.parse(item.children[0].config);
            } catch (e) {
                console.error('Failed to parse layer config', e, item);
            }

            const isLayerSetWithSingleChoice = configJson && configJson.isSingleChoice
                ? configJson.isSingleChoice
                : false;

            return (
                {
                    key: item.uuid,
                    uuid: item.uuid,
                    title: isLayersSet ? () => renderLayerSet(item) : renderLogicalLayer(item, configJson),
                    isLeaf: !isLayersSet,
                    children: isLayersSet ? getTreeData(item.children) : null,
                    isLayerSet: isLayersSet,
                    isSingleChoice: isLayerSetWithSingleChoice,
                    icon: isLayerSetWithSingleChoice ? <span className={'fa fa-square-o invisible'}/> : null,
                    disableCheckbox: isLayerSetWithSingleChoice
                }
            );
        });
    };

    const findParentNode = (treeData, childKey) => {
        for (const parent of treeData) {
            if (parent.children && parent.children.some(child => child.key === childKey)) {
                return parent;
            }
            const foundInChild = findParentNode(parent.children || [], childKey);
            if (foundInChild) return foundInChild;
        }
        return null;
    };

    const treeData = getTreeData(layerDescriptions);

    return (
        <div>
            {
                layerDescriptions && layerDescriptions.length > 0 && (
                    <AmdRequireComponent dependencies={[ 'rc-tree', 'rcTreeCss' ]}>
                        {
                            (rcTree) => {
                                const { 'default': Tree } = rcTree;
                                return (
                                    <Tree
                                        checkedKeys={Object.keys(activeLayerUuids)}
                                        defaultExpandAll={false}
                                        autoExpandParent={true}
                                        draggable={false}
                                        showIcon={true}
                                        selectable={false}
                                        checkable={true}
                                        treeData={treeData}
                                        icon={CheckIcon}
                                        switcherIcon={SwitcherIcon}
                                        onCheck={onCheck}
                                    />
                                );
                            }
                        }
                    </AmdRequireComponent>
                )
            }

            <Overlay
                container={overlayContainer.current}
                target={popupTarget.current}
                show={showInfo}
                placement="bottom"
                rootClose={true}
                onHide={() => setShowInfo(false)}
            >
                {({ placement, arrowProps, show: _show, popper, ...props }) => (
                    <div
                        {...props}
                        className="layer-chooser__info-popup popover bs-popover-bottom"
                    >
                        <div className="arrow" {...arrowProps}/>
                        <div className="popover-body" dangerouslySetInnerHTML={{ __html: infoText.current }}/>
                    </div>
                )}
            </Overlay>
        </div>
    );
};

LayerChooser.propTypes = {
    layerDescriptions: PropTypes.array,
    activeLayerUuids: PropTypes.object,
    onLayersChange: PropTypes.func,
    mapId: PropTypes.string,
};

CheckIcon.propTypes = {
    checked: PropTypes.bool,
};

SwitcherIcon.propTypes = {
    expanded: PropTypes.bool,
    isLeaf: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./js/components/LayerChooser.js